import { skipToken } from '@reduxjs/toolkit/query';
import { sortAsc } from '@strata/helper-functions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useGetMilestonesByMemberDrugQuery } from 'store/api/intakeCaseApi';

dayjs.extend(utc);

const keyDateFromReceivedTime = (receivedTime) => {
    const parsed = dayjs.utc(receivedTime?.trim());
    return parsed.isValid() ? parsed.toISOString() : '';
};

const referralReceivedUtcMs = (receivedTime) => {
    const parsed = dayjs.utc((receivedTime ?? '').trim());
    return parsed.isValid() ? parsed.valueOf() : null;
};

export const getCurrentReferralId = (referralList) => {
    if (!referralList?.length) {
        return undefined;
    }

    const referralsWithReceivedTime = referralList.map((referral) => ({
        referralId: referral.referralId,
        receivedTimeMs: referralReceivedUtcMs(referral.receivedTime) ?? Number.NEGATIVE_INFINITY,
    }));

    referralsWithReceivedTime.sort((leftReferral, rightReferral) => {
        if (leftReferral.receivedTimeMs !== rightReferral.receivedTimeMs) {
            return rightReferral.receivedTimeMs - leftReferral.receivedTimeMs;
        }

        return sortAsc(leftReferral.referralId, rightReferral.referralId);
    });

    return referralsWithReceivedTime[0]?.referralId;
};

// Milestone window end: next referral's received time, or end of today if this is the last referral
export const getIntakeMilestoneEndDateFromReferralList = (referralList, activeReferralId) => {
    if (!activeReferralId || !referralList?.length) {
        return undefined;
    }

    const endOfTodayUtc = () => dayjs.utc().endOf('day').toISOString();

    const referralsSortedByReceivedTime = referralList
        .map((referral) => ({
            referral,
            receivedTimeUtcMs: referralReceivedUtcMs(referral.receivedTime),
        }))
        .filter(({ receivedTimeUtcMs }) => receivedTimeUtcMs != null)
        .sort((left, right) => {
            if (left.receivedTimeUtcMs !== right.receivedTimeUtcMs) {
                return left.receivedTimeUtcMs - right.receivedTimeUtcMs;
            }

            return sortAsc(left.referral.referralId, right.referral.referralId);
        });

    const activeReferralIndex = referralsSortedByReceivedTime.findIndex(
        ({ referral }) => referral.referralId === activeReferralId,
    );

    if (activeReferralIndex === -1) {
        return undefined;
    }

    const nextReferral = referralsSortedByReceivedTime[activeReferralIndex + 1]?.referral;

    if (!nextReferral) {
        return endOfTodayUtc();
    }

    const nextReceived = dayjs.utc((nextReferral.receivedTime ?? '').trim());

    if (!nextReceived.isValid()) {
        return endOfTodayUtc();
    }

    return nextReceived.toISOString();
};

export const buildIntakeCaseMilestoneReferralFieldsFromTreatment = (
    treatmentDetails,
    referralId,
) => {
    if (!treatmentDetails || !referralId) {
        return undefined;
    }

    const { referralList = [], segmentId, memberDetails, productServiceOid } = treatmentDetails;

    const listRow = referralList.find((referral) => referral.referralId === referralId);

    const intakeMilestoneReceivedTime = listRow?.receivedTime ?? null;

    if (
        intakeMilestoneReceivedTime == null ||
        segmentId == null ||
        memberDetails?.memberId == null ||
        memberDetails?.firstName == null ||
        memberDetails?.dateOfBirth == null ||
        productServiceOid == null
    ) {
        return undefined;
    }

    const intakeMilestoneEndDate = getIntakeMilestoneEndDateFromReferralList(
        referralList,
        referralId,
    );

    return {
        segmentId,
        memberId: memberDetails.memberId,
        patientFirstName: memberDetails.firstName,
        patientDateOfBirth: memberDetails.dateOfBirth,
        productServiceOid,
        receivedTime: intakeMilestoneReceivedTime,
        ...(intakeMilestoneEndDate != null ? { endDate: intakeMilestoneEndDate } : {}),
    };
};

const milestoneQueryInput = (referralDetails) => {
    if (referralDetails == null) {
        return null;
    }

    const {
        segmentId,
        memberId,
        patientFirstName,
        patientDateOfBirth,
        productServiceOid,
        receivedTime,
        endDate,
    } = referralDetails;

    const keyDate = keyDateFromReceivedTime(receivedTime);
    const normalizedEndDate = endDate;

    if (
        segmentId == null ||
        memberId == null ||
        patientFirstName == null ||
        patientDateOfBirth == null ||
        productServiceOid == null ||
        !keyDate
    ) {
        return null;
    }

    return {
        segmentId,
        memberId,
        memberFirstName: patientFirstName,
        memberDOB: patientDateOfBirth,
        productServiceOid,
        keyDate,
        endDate: normalizedEndDate,
    };
};

const buildMilestonesByMemberDrugQueryProps = (showIntakeTimeline, referralDetails) => {
    if (!showIntakeTimeline) {
        return skipToken;
    }

    const normalizedInput = milestoneQueryInput(referralDetails);

    return normalizedInput ?? skipToken;
};

export const useIntakeCaseMilestones = ({ referralDetails, showIntakeTimeline }) => {
    const milestonesQueryProps = buildMilestonesByMemberDrugQueryProps(
        showIntakeTimeline,
        referralDetails,
    );

    const queryResult = useGetMilestonesByMemberDrugQuery(milestonesQueryProps);

    return {
        milestones: queryResult.data,
        hasMilestones: Array.isArray(queryResult.data) && queryResult.data.length > 0,
        milestonesFetching: queryResult.isFetching,
        milestonesLoading: queryResult.isLoading,
        milestonesUninitialized: queryResult.isUninitialized,
        milestonesIsError: queryResult.isError,
        milestonesQueryProps,
    };
};
